import { OrderFormData } from '../types';

const createSubmissionHash = (formData: OrderFormData): string => {
  const dataString = `${formData.firstName}_${formData.lastName}_${formData.phone}_${formData.street}_${formData.buildingNumber}_${formData.city}`;
  let hash = 0;
  for (let i = 0; i < dataString.length; i++) {
    const char = dataString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(36);
};

const isSubmittingKey = 'order_form_is_submitting';
const submissionHashKey = 'order_form_submission_hash';

export const saveOrderToGoogleSheets = async (formData: OrderFormData, transactionId: string | null): Promise<void> => {
  try {
    const isSubmitting = sessionStorage.getItem(isSubmittingKey);
    if (isSubmitting === 'true') {
      throw new Error('Form submission already in progress');
    }
    
    const submissionHash = createSubmissionHash(formData);
    const lastSubmissionHash = sessionStorage.getItem(submissionHashKey);
    const lastSubmissionTime = sessionStorage.getItem('order_form_last_submission_time');
    
    if (lastSubmissionHash === submissionHash && lastSubmissionTime) {
      const timeSinceLastSubmission = Date.now() - parseInt(lastSubmissionTime);
      const fiveMinutes = 5 * 60 * 1000;
      
      if (timeSinceLastSubmission < fiveMinutes) {
        throw new Error('Duplicate submission detected');
      }
    }
    
    sessionStorage.setItem(isSubmittingKey, 'true');
    sessionStorage.setItem(submissionHashKey, submissionHash);
    sessionStorage.setItem('order_form_last_submission_time', Date.now().toString());
    
    try {
      const baseUrl = process.env.REACT_APP_GOOGLE_SHEETS_ORDER_URL || 'https://script.google.com/macros/s/AKfycbzNNtYqaqKICWCSCBNmeCohtMbBq_8BY7i4xNA3YDZdoeSG1fm8gXZ8Cw_iMNT5-vrmAg/exec';
      const sheetId = process.env.REACT_APP_GOOGLE_SHEET_ORDER_ID || '1jW1Qg9SXJikBuP1EWI4iPkxbsgWNY4vlM8usWVVHacY';
      
      const timestamp = new Date().toISOString();
      const postData = new FormData();
      postData.append('type', 'order');
      postData.append('sheetId', sheetId);
      postData.append('firstName', formData.firstName);
      postData.append('lastName', formData.lastName);
      postData.append('phone', formData.phone);
      postData.append('street', formData.street);
      postData.append('buildingNumber', formData.buildingNumber);
      if (formData.entrance) postData.append('entrance', formData.entrance);
      if (formData.apartment) postData.append('apartment', formData.apartment);
      postData.append('city', formData.city);
      postData.append('amount', '389');
      if (transactionId) postData.append('transactionId', transactionId);
      postData.append('timestamp', timestamp);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);
      
      try {
        await fetch(baseUrl, {
          method: 'POST',
          mode: 'no-cors',
          body: postData,
          signal: controller.signal,
          cache: 'no-store'
        });
        clearTimeout(timeoutId);
      } catch (fetchError) {
        clearTimeout(timeoutId);
        
        const isOnline = navigator.onLine;
        
        if (fetchError instanceof Error) {
          if (fetchError.name === 'AbortError') {
            if (!isOnline) {
              throw new Error('אין חיבור לאינטרנט. אנא בדוק את החיבור ונסה שוב.');
            }
          } else if (fetchError.message.includes('Failed to fetch') || fetchError.message.includes('Mixed Content')) {
            if (!isOnline) {
              throw new Error('אין חיבור לאינטרנט. אנא בדוק את החיבור ונסה שוב.');
            }
          } else {
            throw fetchError;
          }
        } else {
          if (!isOnline) {
            throw new Error('אין חיבור לאינטרנט. אנא בדוק את החיבור ונסה שוב.');
          }
        }
      }
      
      localStorage.removeItem('orderData');
      localStorage.removeItem('pendingOrderData');
      
    } finally {
      sessionStorage.removeItem(isSubmittingKey);
    }
    
  } catch (error) {
    console.error('❌ Error saving order to Google Sheets:', error);
    
    if (error instanceof Error) {
      if (error.message === 'Duplicate submission detected' || error.message === 'Form submission already in progress') {
        sessionStorage.removeItem(isSubmittingKey);
      }
    }
    
    throw error;
  }
};
